// Block schema для Editorial-фида.
//
// Эта же модель копируется один-в-один в Reverium-приложении
// (src/types/blocks.ts) — синхронизируем руками при изменениях.
//
// Дизайн-философия:
//   • Каждый блок — отдельная единица контента с собственным id и type.
//   • Inline-форматирование внутри текстовых блоков — через узкий
//     HTML-whitelist (<b>, <i>, <u>, <a>, <s>, <code>). Никакого Markdown
//     внутри — editor сам ведёт HTML.
//   • Файлы (изображения, видео) — хранятся в article record как
//     multi-file `media_assets`; в блоке мы храним только filename.
//     Cover остаётся отдельным полем (отдельная file-attachment).
//   • JSON-сериализация — чистая (никаких Date/Map). Это позволяет
//     хранить как PB JSONField и парсить тривиально на клиенте.
//
// Версионирование схемы: добавили `schemaVersion` на корне массива
// блоков — позволит миграции вперёд.

export type BlockId = string;

// ── Inline text alignment & emphasis ──────────────────────────────────────

export type Align = "left" | "center" | "right";

// ── Headings ──────────────────────────────────────────────────────────────

export interface HeadingBlock {
  id: BlockId;
  type: "heading";
  level: 1 | 2 | 3;
  text: string;            // plain text — без inline-форматирования
  align?: Align;           // default 'left'
}

// ── Paragraph ─────────────────────────────────────────────────────────────

export interface ParagraphBlock {
  id: BlockId;
  type: "paragraph";
  html: string;            // sanitized inline HTML (<b><i><u><a><s><code>)
  align?: Align;
  size?: "small" | "regular" | "large" | "lead";  // typography preset
}

// ── Quote / Pullquote ─────────────────────────────────────────────────────

export interface QuoteBlock {
  id: BlockId;
  type: "quote";
  text: string;            // plain text
  attribution?: string;    // "— Director name, year"
  style?: "block" | "pullquote";  // default 'block'
}

// ── Image (single) ────────────────────────────────────────────────────────

export type MediaWidth = "narrow" | "regular" | "wide" | "full";

export interface ImageBlock {
  id: BlockId;
  type: "image";
  /** Filename in article's media_assets — для загруженных файлов. */
  file?: string;
  /** External image URL — для линкования с других сайтов
   *  (Pinterest / Unsplash / etc.) без копирования файлов. Это
   *  предпочтительный способ для прав-чувствительного контента. */
  url?: string;
  alt?: string;            // accessibility
  caption?: string;        // markdown-free plain text under the image
  width?: MediaWidth;      // default 'regular'
  align?: Align;           // default 'center'
  credit?: MediaCredit;
}

// ── Gallery (multi-image) ─────────────────────────────────────────────────

export interface GalleryItem {
  /** filename OR external URL — взаимоисключающие */
  file?: string;
  url?: string;
  alt?: string;
  credit?: MediaCredit;
}

export interface GalleryBlock {
  id: BlockId;
  type: "gallery";
  /** legacy — filenames; новые блоки используют items[]. Если items
   *  есть и не пустой, files игнорируется. */
  files?: string[];
  items?: GalleryItem[];
  layout: "grid-2" | "grid-3" | "grid-4" | "masonry";
  caption?: string;
}

// ── Video (URL embed — Vimeo / YouTube / Instagram / Vimeo Showcase) ─────

export type EmbedProvider =
  | "youtube"
  | "vimeo"
  | "instagram"
  | "pinterest"
  | "soundcloud"
  | "spotify"
  | "bandcamp"
  | "apple_music"
  | "artlist"
  | "twitter"
  | "tiktok"
  | "other";

// MediaCredit — для соблюдения прав авторов: каждый медиа-блок может
// иметь credit (источник + ссылка). Это лучшая практика для редакторского
// контента, где мы не владеем большинством медиа — публикуем по принципу
// curation (Pinterest-style attribution).
export interface MediaCredit {
  text?: string;   // например, "Photo by @nicholas_mihai"
  url?: string;    // ссылка на источник ("View on Pinterest")
}

export interface VideoEmbedBlock {
  id: BlockId;
  type: "video_embed";
  url: string;             // raw URL — клиент detect'ит provider
  provider?: EmbedProvider;  // auto-detect, кэшируется при редактировании
  caption?: string;
  width?: MediaWidth;      // default 'wide'
  aspectRatio?: "16:9" | "9:16" | "4:3" | "1:1";
  credit?: MediaCredit;
}

// ── Audio embed (SoundCloud / Spotify / Bandcamp / Apple Music / Artlist) ─

export interface AudioEmbedBlock {
  id: BlockId;
  type: "audio_embed";
  url: string;             // raw URL
  provider?: EmbedProvider; // auto-detect (soundcloud/spotify/bandcamp/apple_music/artlist/other)
  caption?: string;
  /** Visual style:
   *    'standard' — большой плеер (обложка + название + контролы)
   *    'compact'  — горизонтальный, минимальный (для inline-цитирования)
   */
  style?: "standard" | "compact";
  width?: MediaWidth;
  credit?: MediaCredit;
}

// ── Audio file (uploaded mp3/m4a) ─────────────────────────────────────────

export interface AudioFileBlock {
  id: BlockId;
  type: "audio_file";
  file: string;
  caption?: string;
  poster?: string;          // обложка (image filename)
  width?: MediaWidth;
  credit?: MediaCredit;
}

// ── Link card (Open Graph-style preview for external articles) ────────────
//
// Когда хочется процитировать внешнюю статью / лонгрид без копирования
// контента — embed карточка с превью, заголовком и описанием.

export interface LinkCardBlock {
  id: BlockId;
  type: "link_card";
  url: string;
  title?: string;          // если admin задал вручную, иначе OG fetch
  description?: string;
  image?: string;          // thumbnail URL (внешний)
  siteName?: string;
}

// ── Video (uploaded mp4) ──────────────────────────────────────────────────

export interface VideoFileBlock {
  id: BlockId;
  type: "video_file";
  file: string;            // filename in media_assets
  poster?: string;         // optional separate poster image (also filename)
  caption?: string;
  width?: MediaWidth;
  autoplay?: boolean;
  loop?: boolean;
  muted?: boolean;
  credit?: MediaCredit;
}

// ── Caption / Note (standalone subtle text) ───────────────────────────────

export interface CaptionBlock {
  id: BlockId;
  type: "caption";
  text: string;
  align?: Align;
}

// ── Divider ───────────────────────────────────────────────────────────────

export interface DividerBlock {
  id: BlockId;
  type: "divider";
  style: "line" | "dots" | "asterisks" | "blank";
}

// ── List (bulleted / numbered) ────────────────────────────────────────────

export interface ListBlock {
  id: BlockId;
  type: "list";
  ordered: boolean;
  items: string[];         // each item is plain text (Phase 2: html)
}

// ── Spacer ────────────────────────────────────────────────────────────────

export interface SpacerBlock {
  id: BlockId;
  type: "spacer";
  height: 16 | 24 | 32 | 48 | 64 | 96 | 128;  // px presets
}

// ── CTA (Call to action) ──────────────────────────────────────────────────

export interface CtaBlock {
  id: BlockId;
  type: "cta";
  label: string;
  url: string;
  style?: "primary" | "secondary" | "subtle";
  align?: Align;
}

// ── Hero (special: first-block, full-width media + title overlay) ─────────

export interface HeroBlock {
  id: BlockId;
  type: "hero";
  mediaKind: "image" | "video_embed" | "video_file";
  media: string;           // filename OR url depending on mediaKind
  title?: string;
  subtitle?: string;
  overlayPosition?: "bottom-left" | "bottom-center" | "center" | "top-left";
  overlayTheme?: "light" | "dark";  // text color over media
}

// ── Embed: arbitrary iframe (Twitter, SoundCloud, etc.) ───────────────────

export interface EmbedBlock {
  id: BlockId;
  type: "embed";
  html: string;            // iframe HTML (admin trusted)
  caption?: string;
}

// ── Union ─────────────────────────────────────────────────────────────────

export type Block =
  | HeadingBlock
  | ParagraphBlock
  | QuoteBlock
  | ImageBlock
  | GalleryBlock
  | VideoEmbedBlock
  | VideoFileBlock
  | AudioEmbedBlock
  | AudioFileBlock
  | LinkCardBlock
  | CaptionBlock
  | DividerBlock
  | ListBlock
  | SpacerBlock
  | CtaBlock
  | HeroBlock
  | EmbedBlock;

export type BlockType = Block["type"];

export const BLOCK_TYPES: BlockType[] = [
  "heading",
  "paragraph",
  "quote",
  "image",
  "gallery",
  "video_embed",
  "video_file",
  "audio_embed",
  "audio_file",
  "link_card",
  "caption",
  "divider",
  "list",
  "spacer",
  "cta",
  "hero",
  "embed",
];

// ── Document wrapper ──────────────────────────────────────────────────────

export interface BlockDocument {
  schemaVersion: 1;
  blocks: Block[];
}

export function emptyDocument(): BlockDocument {
  return { schemaVersion: 1, blocks: [] };
}

// ── Block factory ─────────────────────────────────────────────────────────

// Crypto-random id (12 chars base36 ~ 62 bits).
export function newId(): BlockId {
  const a = new Uint8Array(8);
  crypto.getRandomValues(a);
  let id = "";
  for (let i = 0; i < a.length; i++) id += a[i].toString(36).padStart(2, "0");
  return id.slice(0, 12);
}

export function makeBlock(type: BlockType): Block {
  const id = newId();
  switch (type) {
    case "heading":     return { id, type: "heading", level: 2, text: "" };
    case "paragraph":   return { id, type: "paragraph", html: "" };
    case "quote":       return { id, type: "quote", text: "" };
    case "image":       return { id, type: "image", width: "regular" };
    case "gallery":     return { id, type: "gallery", items: [], layout: "grid-3" };
    case "video_embed": return { id, type: "video_embed", url: "", width: "wide", aspectRatio: "16:9" };
    case "video_file":  return { id, type: "video_file", file: "", width: "regular" };
    case "audio_embed": return { id, type: "audio_embed", url: "", style: "standard", width: "regular" };
    case "audio_file":  return { id, type: "audio_file", file: "", width: "regular" };
    case "link_card":   return { id, type: "link_card", url: "" };
    case "caption":     return { id, type: "caption", text: "", align: "center" };
    case "divider":     return { id, type: "divider", style: "line" };
    case "list":        return { id, type: "list", ordered: false, items: [""] };
    case "spacer":      return { id, type: "spacer", height: 48 };
    case "cta":         return { id, type: "cta", label: "Read more", url: "", style: "primary" };
    case "hero":        return { id, type: "hero", mediaKind: "image", media: "", overlayPosition: "bottom-left", overlayTheme: "light" };
    case "embed":       return { id, type: "embed", html: "" };
  }
}

// ── Validation: minimal, used for "Publish" gate ──────────────────────────

export function validateBlock(b: Block): string | null {
  switch (b.type) {
    case "heading":
      return b.text.trim() ? null : "Empty heading";
    case "paragraph":
      return stripHtml(b.html).trim() ? null : "Empty paragraph";
    case "quote":
      return b.text.trim() ? null : "Empty quote";
    case "image":
      return (b.file || b.url) ? null : "Add a file or URL";
    case "video_file":
    case "audio_file":
      return b.file ? null : "No media file";
    case "video_embed":
    case "audio_embed":
      return b.url ? null : "No embed URL";
    case "link_card":
      return b.url ? null : "No URL";
    case "gallery": {
      const has =
        (b.items && b.items.length > 0) ||
        (b.files && b.files.length > 0);
      return has ? null : "Empty gallery";
    }
    case "list":
      return b.items.some((i) => i.trim()) ? null : "Empty list";
    case "cta":
      return b.label.trim() && b.url ? null : "CTA needs label and URL";
    case "hero":
      return b.media ? null : "Hero has no media";
    case "embed":
      return b.html.trim() ? null : "Empty embed";
    case "caption":
      return b.text.trim() ? null : "Empty caption";
    case "divider":
    case "spacer":
      return null;
  }
}

function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, "");
}

// ── Embed provider detection (used by editor + renderer) ──────────────────

export function detectProvider(url: string): EmbedProvider {
  const u = url.toLowerCase();
  if (u.includes("youtube.com") || u.includes("youtu.be")) return "youtube";
  if (u.includes("vimeo.com")) return "vimeo";
  if (u.includes("instagram.com")) return "instagram";
  if (u.includes("pinterest.")) return "pinterest";
  if (u.includes("soundcloud.com")) return "soundcloud";
  if (u.includes("open.spotify.com") || u.includes("spotify.link")) return "spotify";
  if (u.includes("bandcamp.com")) return "bandcamp";
  if (u.includes("music.apple.com")) return "apple_music";
  if (u.includes("artlist.io")) return "artlist";
  if (u.includes("twitter.com") || u.includes("x.com")) return "twitter";
  if (u.includes("tiktok.com")) return "tiktok";
  return "other";
}

// ── Embed URL builders ───────────────────────────────────────────────────
//
// Зеркало `src/types/blocks.ts` в Reverium-app: одинаковая логика чтобы
// preview в admin'е выглядел так же как у читателя. Возвращают URL для
// iframe.src или null если provider не embed-able напрямую (Twitter).

export function youtubeEmbedUrl(rawUrl: string): string | null {
  try {
    const u = new URL(rawUrl);
    let id: string | null = null;
    if (u.hostname.endsWith("youtu.be")) id = u.pathname.slice(1);
    else if (u.pathname.startsWith("/watch")) id = u.searchParams.get("v");
    else if (u.pathname.startsWith("/shorts/")) id = u.pathname.split("/")[2] || null;
    else if (u.pathname.startsWith("/embed/")) id = u.pathname.split("/")[2] || null;
    return id ? `https://www.youtube-nocookie.com/embed/${id.replace(/[^A-Za-z0-9_-]/g, "")}` : null;
  } catch {
    return null;
  }
}

export function vimeoEmbedUrl(rawUrl: string): string | null {
  try {
    const u = new URL(rawUrl);
    const segments = u.pathname.split("/").filter(Boolean);
    // Vimeo: /VIDEO_ID, /showcase/SHOW/VIDEO, /channels/CH/VIDEO_ID
    const id = segments.find((s) => /^\d+$/.test(s));
    return id ? `https://player.vimeo.com/video/${id}` : null;
  } catch {
    return null;
  }
}

export function instagramEmbedUrl(rawUrl: string): string | null {
  try {
    const u = new URL(rawUrl);
    if (!u.hostname.includes("instagram.com")) return null;
    const cleanPath = u.pathname.replace(/\/$/, "");
    return `https://www.instagram.com${cleanPath}/embed/`;
  } catch {
    return null;
  }
}

export function tiktokEmbedUrl(rawUrl: string): string | null {
  try {
    const u = new URL(rawUrl);
    if (!u.hostname.includes("tiktok.com")) return null;
    const m = u.pathname.match(/\/video\/(\d+)/);
    return m ? `https://www.tiktok.com/embed/v2/${m[1]}` : null;
  } catch {
    return null;
  }
}

export function soundcloudEmbedUrl(rawUrl: string): string {
  return `https://w.soundcloud.com/player/?url=${encodeURIComponent(rawUrl)}&color=%23b8864a&auto_play=false&hide_related=true&show_comments=false&show_user=true&show_reposts=false&show_teaser=false`;
}

export function spotifyEmbedUrl(rawUrl: string): string | null {
  try {
    const u = new URL(rawUrl);
    if (!u.hostname.includes("spotify")) return null;
    // open.spotify.com/track/ID → open.spotify.com/embed/track/ID
    if (u.pathname.startsWith("/embed/")) return rawUrl;
    return `https://open.spotify.com/embed${u.pathname}`;
  } catch {
    return null;
  }
}

export function appleMusicEmbedUrl(rawUrl: string): string | null {
  try {
    const u = new URL(rawUrl);
    if (!u.hostname.includes("music.apple.com")) return null;
    return rawUrl.replace("music.apple.com", "embed.music.apple.com");
  } catch {
    return null;
  }
}

export function bandcampEmbedUrl(rawUrl: string): string {
  // Bandcamp полноценный embed требует track_id (oEmbed-запрос). Для admin
  // preview используем raw URL — браузер откроет страницу. На production
  // рендере (Reverium reader) попробуем oEmbed позже.
  return rawUrl;
}

/** Универсальный builder — возвращает URL для iframe.src или null если не embed. */
export function embedUrlFor(provider: EmbedProvider, url: string): string | null {
  switch (provider) {
    case "youtube":     return youtubeEmbedUrl(url);
    case "vimeo":       return vimeoEmbedUrl(url);
    case "instagram":   return instagramEmbedUrl(url);
    case "tiktok":      return tiktokEmbedUrl(url);
    case "soundcloud":  return soundcloudEmbedUrl(url);
    case "spotify":     return spotifyEmbedUrl(url);
    case "apple_music": return appleMusicEmbedUrl(url);
    case "bandcamp":    return bandcampEmbedUrl(url);
    case "pinterest":
    case "artlist":
    case "twitter":
    case "other":
    default:            return null; // нет надёжного embed; покажем link card
  }
}

/** Aspect ratio hint для разных провайдеров (для preview iframe). */
export function defaultAspectFor(provider: EmbedProvider): "16:9" | "9:16" | "4:3" | "1:1" {
  if (provider === "tiktok" || provider === "instagram") return "9:16";
  if (provider === "soundcloud" || provider === "spotify" || provider === "apple_music") {
    return "4:3"; // аудио-плеер компактнее, 4:3 ближе по высоте
  }
  return "16:9";
}

// ── HTML sanitization (paragraph block) ───────────────────────────────────
//
// Узкий whitelist для inline-форматирования. Никаких <script>, <iframe>,
// inline-стилей, on* атрибутов. <a> разрешён только с http(s):// URL,
// добавляется rel/target автоматически на рендере.
const ALLOWED_INLINE_TAGS = new Set(["b", "strong", "i", "em", "u", "s", "code", "a", "br"]);
const ALLOWED_ATTRS_PER_TAG: Record<string, Set<string>> = {
  a: new Set(["href"]),
};

export function sanitizeInlineHtml(html: string): string {
  if (!html) return "";
  // Парсим через DOMParser — это и валидация и нормализация одновременно.
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, "text/html");
  const root = doc.body.firstElementChild;
  if (!root) return "";
  return sanitizeNode(root).innerHTML;
}

function sanitizeNode(el: Element): Element {
  const children = Array.from(el.childNodes);
  for (const child of children) {
    if (child.nodeType === Node.TEXT_NODE) continue;
    if (child.nodeType !== Node.ELEMENT_NODE) {
      child.parentNode?.removeChild(child);
      continue;
    }
    const c = child as Element;
    const tag = c.tagName.toLowerCase();
    if (!ALLOWED_INLINE_TAGS.has(tag)) {
      // unwrap: replace element with its children (keep text content)
      const parent = c.parentNode!;
      while (c.firstChild) parent.insertBefore(c.firstChild, c);
      parent.removeChild(c);
      continue;
    }
    // strip attributes
    const allowed = ALLOWED_ATTRS_PER_TAG[tag] || new Set<string>();
    for (const attr of Array.from(c.attributes)) {
      if (!allowed.has(attr.name.toLowerCase())) c.removeAttribute(attr.name);
    }
    // sanitize href
    if (tag === "a") {
      const href = (c.getAttribute("href") || "").trim();
      if (!/^https?:\/\//i.test(href)) {
        c.removeAttribute("href");
      }
    }
    sanitizeNode(c);
  }
  return el;
}
